import React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

export default function DateTimePicker({ value, onChange, testid = "datetime" }) {
  const dt = value ? new Date(value) : null;
  const [open, setOpen] = React.useState(false);
  const today = React.useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  const emit = (d) => onChange(d.toISOString());

  // Never blank: an empty value defaults to the current date + time (next whole minute).
  React.useEffect(() => {
    if (value) return;
    const n = new Date();
    n.setSeconds(0, 0);
    n.setMinutes(n.getMinutes() + 1);
    emit(n);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDatePart = (day) => {
    if (!day) return;
    const base = dt ? new Date(dt) : (() => { const n = new Date(); n.setHours(12, 0, 0, 0); return n; })();
    base.setFullYear(day.getFullYear(), day.getMonth(), day.getDate());
    emit(base);
    setOpen(false);
  };

  // Free-form time entry (any minute) - no preset slots.
  const timeStr = dt
    ? `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`
    : "";
  const setTimeStr = (str) => {
    if (!str) return;
    const [h, m] = str.split(":").map((x) => parseInt(x, 10) || 0);
    const base = dt ? new Date(dt) : (() => { const n = new Date(); n.setHours(12, 0, 0, 0); return n; })();
    base.setHours(h, m, 0, 0);
    emit(base);
  };

  const dateLabel = dt ? dt.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "Pick date";

  return (
    <div className="flex gap-2" data-testid={`${testid}-wrap`}>
      {/* DATE - click a day on the calendar */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`${testid}-date-trigger`}
            className="flex-1 min-w-0 flex items-center gap-2 bg-race-bg border border-race-line focus:border-race-red outline-none px-3 py-2.5 rounded-sm text-left transition-colors hover:border-race-red/60">
            <CalendarIcon size={16} className="text-race-red shrink-0" />
            <span className={`text-sm truncate ${dt ? "text-race-txt" : "text-race-muted"}`}>{dateLabel}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-race-surface border-race-line" align="start" data-testid={`${testid}-calendar`}>
          <Calendar mode="single" selected={dt || undefined} onSelect={setDatePart} initialFocus
            disabled={{ before: today }} fromMonth={today}
            modifiersClassNames={{ today: "ring-2 ring-race-red ring-offset-1 ring-offset-race-surface font-bold text-race-red rounded-md" }} />
        </PopoverContent>
      </Popover>

      {/* TIME - type any custom time (minute precision); native picker provides the clock */}
      <div className="shrink-0">
        <input type="time" step="60" data-testid={`${testid}-time`} value={timeStr}
          onChange={(e) => setTimeStr(e.target.value)}
          style={{ colorScheme: "dark" }}
          className={`bg-race-bg border border-race-line focus:border-race-red outline-none px-3 py-2.5 rounded-sm text-sm transition-colors hover:border-race-red/60 [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer ${dt ? "text-race-txt" : "text-race-muted"}`} />
      </div>
    </div>
  );
}
